/* eslint-disable max-lines */
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Tooltip from '@base/Tooltip/Tooltip';
import { getIsLoggedIn } from '@api/selectors/auth';
import useSidenavFlags from '@api/hooks/useSidenavFlags';
import {
  getIsPracticeAvailable,
  getIsNoTestGoal,
  getIsSyllabusV1Enabled,
  getIsPrimeEnabled,
  getIsIconicAvailable
} from '@api/selectors/topology';
import useTopology from '@api/hooks/topology/useTopology';
import { Fonts, Spacings } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { sideNavFlags as sideNavFlagsConst } from '@constants/index';
import cookies from '@utils/cookies';
import {
  getIsCompeteEnabled,
  getIsGTPGoal,
  getIsReferralRevampEnabled,
  getIsTestRevampGoal
} from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import algos from '@utils/algos';
import NavNotes from '@icon/notes/NavNotes';
import PrintedNotesIcon from '@cont/AddressCollection/PrintedNotes/PrintedNotesIcon';
import { localStorageAvailable } from '@utils/localStorage';
import {
  isPrintedNotesEnabled,
  printedNotesPageName
} from '@cont/AddressCollection/constants';
import {
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro,
  getIsSubscribedToLite
} from '@api/selectors/subscription';
import { segment } from '@actions/analytics';
import { SELF_STUDY_UPSELL_ITEM_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useMounted from '@hooks/useMounted';
import { StyH7 } from './CommonComps';
import plusNavBarItems from './plusNavBarItems';
import { getIsBlueDotshownForTestRevamp, goalHref } from './commonUtils';
import { Item } from './NavBarItems';
import SideNavPractice from './PracticeIcon';
import TestsIcon from './TestsIcon';
import SyllabusIcon from './SyllabusIcon';
import DoubtsIcon from './DoubtsIcon';
import BrowseIcon from './BrowseIcon';
import OtherCoursesIcon from './OtherCoursesIcon';
import FreeLiveClassesIcon from './FreeLiveClassesIcon';
import LiveMentoringIcon from './LiveMentoringIcon';
import SideNavSaved from './SavedIcon';
import CompeteIcon from './CompeteIcon';
import PrimeIcon from './PrimeIcon';
import LockIcon from './LockIcon';
import ReferEarnIcon from './ReferEarnIcon';

const StySideNavPractice = styled(SideNavPractice)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyBrowseIcon = styled(BrowseIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyTestsIcon = styled(TestsIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StySyllabusIcon = styled(SyllabusIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyNotesIcon = styled(NavNotes)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyDoubtsIcon = styled(DoubtsIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyFreeLiveClassesIcon = styled(FreeLiveClassesIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyPrintedNotesIcon = styled(PrintedNotesIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyOtherCoursesIcon = styled(OtherCoursesIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyLiveMentoring = styled(LiveMentoringIcon)`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-right: ${Spacings.SPACING_3B};
`;

const StyPrimeIcon = styled(PrimeIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StySideNavSaved = styled(SideNavSaved)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyCompete = styled(CompeteIcon)`
  margin-right: ${Spacings.SPACING_3B};
`;

const StyLockIcon = styled(LockIcon)`
  flex-shrink: 0;
  .lockBg {
    fill: var(--color-divider);
  }
  .lockBody {
    stroke: var(--color-text-secondary);
  }
`;

const LockWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-left: ${Spacings.SPACING_2B};
`;

const Label = styled.span`
  white-space: nowrap;
`;

const LabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;
`;

const ReferralNewTag = styled.span`
  flex-shrink: 0;
  margin-left: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_1} ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-accent-blue);
  color: var(--color-i-white);
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_4B};
  letter-spacing: 0.02em;
`;

const StyTooltip = styled(Tooltip)`
  .tooltip {
    color: var(--color-text-secondary);
    font-size: ${Fonts.H7};
    font-weight: ${Fonts.SEMIBOLD};
    line-height: ${Spacings.SPACING_6B};
  }
  .MuiTooltip-arrow {
    color: var(--color-divider);
  }
`;

const StyItem = styled(Item)`
  &:hover .lockBg {
    fill: var(--color-text-secondary);
  }
  &:hover .lockBody {
    stroke: var(--color-base-1);
  }
`;

const StyReferEarn = styled(ReferEarnIcon)`
  flex-shrink: 0;
  margin-right: ${Spacings.SPACING_3B};
`;

const SideNavItemIcon = ({
  icon: Icon,
  keepIconColor,
  isSelected,
  hovColor
}) => {
  if (!Icon) return null;
  if (keepIconColor) {
    return <Icon />;
  }
  return (
    <Icon
      size="20px"
      color={isSelected ? hovColor : 'var(--color-text-secondary)'}
      className="sideNavIcon"
    />
  );
};

const practiceUrls = [
  `${goalHref}/practice`,
  `${goalHref}/practice/topics`,
  `${goalHref}/practice/topics/[topicUID]`,
  `${goalHref}/practice/topics/[topicUID]/concept/[conceptUID]`
];

const IconDot = styled.span`
  width: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_2B};
  border-radius: 50%;
  background-color: var(--color-i-blue);
  position: absolute;
  top: 25%;
  left: 12%;
  border: 1.6px solid var(--color-text-tertiary);
  &:hover {
    background-color: var(--color-i-blue) !important;
  }
`;

const getSelfStudyMap = ({
  route,
  goalUID,
  handleClick,
  isPracticeAvailable,
  isCompeteEnabled,
  isNoTestGoal,
  hasPlusSubscription,
  isSubscribedToLite,
  liveMentoringEnabled,
  isCatalogueFeatureAvailable,
  selectedNav,
  goalAs,
  isK12,
  isAADEnabledFlag,
  isNotesEnabledFlag,
  isLoggedIn,
  isSyllabusV1Enabled,
  isGTP,
  showBlueDotForTestRevamp,
  isRecordedGoal,
  isPrintedNotesEnabledFlag,
  hasOfflineSubscription,
  isPrimeEnabledFlag,
  isMentorshipLocked,
  isPrintedNotesLocked,
  isReferralRevampEnabled
}) => {
  const isPracticeURL = practiceUrls.includes(route);

  const isTestSeriesURL = route === `${goalHref}/test-series`;

  const isCompeteURL = route === `${goalHref}/compete`;

  const isPrimeURL = route === `${goalHref}/prime`;

  const isDoubtsAndSolutionVisited = cookies.readCookie(
    'doubtsAndSolutionVisited',
    false
  );
  const isSyllabusVisited = cookies.readCookie('syllabusVisited', false);
  const isNotesVisited = cookies.readCookie('notesVisited', false);

  const isPrintedNotesVisited = localStorageAvailable()
    ? localStorage.getItem('isPrintedNotesVisited')
    : false;
  const practiceHref = `${goalHref}/practice`;
  const practiceAs = `${goalAs}/practice`;
  const settingsReferralHref = '/settings/referral';
  const goalReferralHref = `${goalHref}/referral`;
  const goalReferralAs = `${goalAs}/referral`;
  const referralNavHref = isReferralRevampEnabled
    ? goalReferralHref
    : settingsReferralHref;
  const referralNavAs = isReferralRevampEnabled
    ? goalReferralAs
    : settingsReferralHref;
  const isReferralSelected =
    route === settingsReferralHref ||
    route === goalReferralHref ||
    selectedNav === plusNavBarItems.REFER_EARN;

  const attachNavClick = (item) => ({
    ...item,
    itemHandleClick: handleClick(item.dataId, item.href, item.as)
  });

  const referEarnNavItem = attachNavClick({
    label: 'Refer & earn',
    dataId: plusNavBarItems.REFER_EARN,
    isSelected: isReferralSelected,
    href: referralNavHref,
    as: referralNavAs,
    key: plusNavBarItems.REFER_EARN,
    isShown: isReferralRevampEnabled,
    icon: StyReferEarn,
    keepIconColor: true,
    hovColor: 'var(--color-i-blue)',
    showNewTag: true
  });

  const selfStudyMap = [
    {
      label: 'Browse',
      dataId: plusNavBarItems.BROWSE,
      isSelected:
        route === `${goalHref}/browse` ||
        selectedNav === plusNavBarItems.BROWSE,
      href: `${goalHref}/browse`,
      as: `${goalAs}/browse`,
      itemHandleClick: handleClick(
        plusNavBarItems.BROWSE,
        `${goalHref}/browse`,
        `${goalAs}/browse`
      ),
      key: plusNavBarItems.BROWSE,
      isShown: true,
      icon: StyBrowseIcon,
      hovColor: 'var(--color-i-turquoise-6)'
    },
    referEarnNavItem,
    {
      label: 'Compete',
      dataId: plusNavBarItems.COMPETE,
      isSelected: isCompeteURL || selectedNav === plusNavBarItems.COMPETE,
      href: `${goalHref}/compete`,
      as: `${goalAs}/compete`,
      itemHandleClick: handleClick(
        plusNavBarItems.COMPETE,
        `${goalHref}/compete`,
        `${goalAs}/compete`
      ),
      key: plusNavBarItems.COMPETE,
      isShown: isCompeteEnabled,
      icon: StyCompete,
      hovColor: 'var(--color-i-blue-20)'
    },
    {
      label: goalUID === 'SDDOC' ? plusNavBarItems.Q_BANK : 'Practice',
      dataId: plusNavBarItems.PRACTICE,
      isSelected: isPracticeURL,
      href: practiceHref,
      as: practiceAs,
      itemHandleClick: handleClick(
        plusNavBarItems.PRACTICE,
        practiceHref,
        practiceAs
      ),
      key: plusNavBarItems.PRACTICE,
      isShown: isPracticeAvailable,
      icon: StySideNavPractice,
      hovColor: 'var(--color-i-violet-12)'
    },
    {
      label: 'Tests',
      dataId: plusNavBarItems.TEST_SERIES,
      isSelected: isTestSeriesURL,
      href: `${goalHref}/test-series`,
      as: `${goalAs}/test-series`,
      itemHandleClick: handleClick(
        plusNavBarItems.TEST_SERIES,
        `${goalHref}/test-series`,
        `${goalAs}/test-series`
      ),
      key: plusNavBarItems.TEST_SERIES,
      isShown: !isNoTestGoal,
      icon: StyTestsIcon,
      showDot: showBlueDotForTestRevamp,
      hovColor: 'var(--color-i-blue-20)'
    },
    {
      label: isRecordedGoal ? 'Video Lessons' : plusNavBarItems.PLAYLIST,
      dataId: plusNavBarItems.SYLLABUS,
      isSelected: selectedNav === plusNavBarItems.SYLLABUS,
      href: `${goalHref}/topics`,
      as: `${goalAs}/topics`,
      itemHandleClick: handleClick(
        isRecordedGoal
          ? plusNavBarItems.VIDEO_LESSONS
          : plusNavBarItems.SYLLABUS,
        `${goalHref}/topics`,
        `${goalAs}/topics`
      ),
      key: plusNavBarItems.SYLLABUS,
      isShown: true,
      icon: StySyllabusIcon,
      showDot: isSyllabusV1Enabled && !isSyllabusVisited,
      hovColor: 'var(--color-i-green-8)'
    },
    {
      label: 'Doubts & solutions',
      dataId: plusNavBarItems.DOUBTS_AND_SOLUTIONS,
      isSelected: selectedNav === plusNavBarItems.DOUBTS_AND_SOLUTIONS,
      href: `${goalHref}/doubts-and-solutions`,
      as: `${goalAs}/doubts-and-solutions`,
      itemHandleClick: handleClick(
        plusNavBarItems.DOUBTS_AND_SOLUTIONS,
        `${goalHref}/doubts-and-solutions`,
        `${goalAs}/doubts-and-solutions`
      ),
      key: plusNavBarItems.DOUBTS_AND_SOLUTIONS,
      isShown:
        !isLoggedIn ||
        isCatalogueFeatureAvailable ||
        isAADEnabledFlag ||
        isSubscribedToLite,
      icon: StyDoubtsIcon,
      showDot: !isDoubtsAndSolutionVisited,
      hovColor: 'var(--color-i-green-9)'
    },
    {
      label: 'Notes',
      dataId: plusNavBarItems.NOTES,
      isSelected: selectedNav === plusNavBarItems.NOTES,
      href: `${goalHref}/notes`,
      as: `${goalAs}/notes`,
      itemHandleClick: handleClick(
        plusNavBarItems.NOTES,
        `${goalHref}/notes`,
        `${goalAs}/notes`
      ),
      key: plusNavBarItems.NOTES,
      isShown:
        isLoggedIn &&
        (hasPlusSubscription || hasOfflineSubscription) &&
        isNotesEnabledFlag,
      icon: StyNotesIcon,
      showDot: !isNotesVisited,
      hovColor: 'var(--color-i-yellow-4)'
    },
    {
      label: 'Free live classes',
      dataId: plusNavBarItems.FREE_CLASSES_AND_TESTS,
      isSelected: selectedNav === plusNavBarItems.FREE_CLASSES_AND_TESTS,
      href: `${goalHref}/classes`,
      as: `${goalAs}/classes`,
      itemHandleClick: handleClick(
        plusNavBarItems.FREE_CLASSES_AND_TESTS,
        `${goalHref}/classes`,
        `${goalAs}/classes`
      ),
      key: plusNavBarItems.FREE_CLASSES_AND_TESTS,
      isShown: !isPrimeEnabledFlag,
      icon: StyFreeLiveClassesIcon,
      hovColor: 'var(--color-i-red-1)'
    },
    {
      label: isK12 ? 'Live homework help' : '1:1 Live mentorship',
      dataId: plusNavBarItems.ONE_TO_ONE_LIVE_MENTORSHIP,
      isSelected: selectedNav === plusNavBarItems.ONE_TO_ONE_LIVE_MENTORSHIP,
      href: isMentorshipLocked
        ? `${goalHref}/subscribe`
        : `${goalHref}/one-to-one-livementorship`,
      as: isMentorshipLocked
        ? `${goalAs}/subscribe`
        : `${goalAs}/one-to-one-livementorship`,
      itemHandleClick: handleClick(
        plusNavBarItems.ONE_TO_ONE_LIVE_MENTORSHIP,
        isMentorshipLocked
          ? `${goalHref}/subscribe`
          : `${goalHref}/one-to-one-livementorship`,
        isMentorshipLocked
          ? `${goalAs}/subscribe`
          : `${goalAs}/one-to-one-livementorship`
      ),
      linkHandleClick: handleClick(plusNavBarItems.ONE_TO_ONE_LIVE_MENTORSHIP),
      hasPlusSubscription,
      key: plusNavBarItems.ONE_TO_ONE_LIVE_MENTORSHIP,
      isShown: liveMentoringEnabled || isMentorshipLocked,
      icon: StyLiveMentoring,
      isLocked: isMentorshipLocked,
      upsellType: isMentorshipLocked
        ? sideNavFlagsConst.ONE_TO_ONE_LIVE_MENTORING
        : null,
      hovColor: 'var(--color-i-blue-18)'
    },
    {
      label: printedNotesPageName,
      dataId: plusNavBarItems.PRINTED_NOTES,
      isSelected: selectedNav === plusNavBarItems.PRINTED_NOTES,
      href: isPrintedNotesLocked
        ? `${goalHref}/subscribe`
        : `${goalHref}/address-collection`,
      as: isPrintedNotesLocked
        ? `${goalAs}/subscribe`
        : `${goalAs}/address-collection`,
      itemHandleClick: handleClick(
        plusNavBarItems.PRINTED_NOTES,
        isPrintedNotesLocked
          ? `${goalHref}/subscribe`
          : `${goalHref}/address-collection`,
        isPrintedNotesLocked
          ? `${goalAs}/subscribe`
          : `${goalAs}/address-collection`
      ),
      key: plusNavBarItems.PRINTED_NOTES,
      isShown:
        (isLoggedIn && isPrintedNotesEnabledFlag) || isPrintedNotesLocked,
      icon: StyPrintedNotesIcon,
      showDot: !isPrintedNotesLocked && !isPrintedNotesVisited,
      isLocked: isPrintedNotesLocked,
      upsellType: isPrintedNotesLocked ? sideNavFlagsConst.PRINTED_NOTES : null,
      hovColor: 'var(--color-i-yellow-7)'
    },
    {
      label: 'Other courses',
      dataId: plusNavBarItems.FREE_PLATFORM,
      isSelected: selectedNav === plusNavBarItems.FREE_PLATFORM,
      href: `${goalHref}/free-platform`,
      as: `${goalAs}/free-platform`,
      itemHandleClick: handleClick(
        plusNavBarItems.FREE_PLATFORM,
        `${goalHref}/free-platform`,
        `${goalAs}/free-platform`
      ),
      key: plusNavBarItems.FREE_PLATFORM,
      isShown: !(hasPlusSubscription || hasOfflineSubscription),
      icon: StyOtherCoursesIcon,
      hovColor: 'var(--color-i-gray-17)'
    },
    {
      label: 'Prime',
      dataId: plusNavBarItems.PRIME,
      isSelected: isPrimeURL || selectedNav === plusNavBarItems.PRIME,
      href: `${goalHref}/prime`,
      as: `${goalAs}/prime`,
      itemHandleClick: handleClick(
        plusNavBarItems.PRIME,
        `${goalHref}/prime`,
        `${goalAs}/prime`
      ),
      key: plusNavBarItems.PRIME,
      isShown: isPrimeEnabledFlag,
      icon: StyPrimeIcon,
      hovColor: 'var(--color-i-violet-15)'
    }
  ];

  const gtpSelfStudyMap = [
    {
      label: 'Browse',
      dataId: plusNavBarItems.BROWSE,
      isSelected:
        route === `${goalHref}/browse` ||
        selectedNav === plusNavBarItems.BROWSE,
      href: `${goalHref}/browse`,
      as: `${goalAs}/browse`,
      itemHandleClick: handleClick(
        plusNavBarItems.BROWSE,
        `${goalHref}/browse`,
        `${goalAs}/browse`
      ),
      key: plusNavBarItems.BROWSE,
      isShown: true,
      icon: StyBrowseIcon,
      hovColor: 'var(--color-i-turquoise-6)'
    },
    {
      label: 'Video lessons',
      dataId: plusNavBarItems.SYLLABUS,
      isSelected: selectedNav === plusNavBarItems.SYLLABUS,
      href: `${goalHref}/topics`,
      as: `${goalAs}/topics`,
      itemHandleClick: handleClick(
        plusNavBarItems.SYLLABUS,
        `${goalHref}/topics`,
        `${goalAs}/topics`
      ),
      key: plusNavBarItems.SYLLABUS,
      isShown: true,
      icon: StyFreeLiveClassesIcon,
      showDot: isSyllabusV1Enabled && !isSyllabusVisited,
      hovColor: 'var(--color-i-red-1)'
    },
    {
      label: 'Practice',
      dataId: plusNavBarItems.PRACTICE,
      isSelected: isPracticeURL,
      href: practiceHref,
      as: practiceAs,
      itemHandleClick: handleClick(
        plusNavBarItems.PRACTICE,
        practiceHref,
        practiceAs
      ),
      key: plusNavBarItems.PRACTICE,
      isShown: true,
      icon: StySideNavPractice,
      hovColor: 'var(--color-i-violet-12)'
    },
    {
      label: 'Tests',
      dataId: plusNavBarItems.TEST_SERIES,
      isSelected: isTestSeriesURL,
      href: `${goalHref}/test-series`,
      as: `${goalAs}/test-series`,
      itemHandleClick: handleClick(
        plusNavBarItems.TEST_SERIES,
        `${goalHref}/test-series`,
        `${goalAs}/test-series`
      ),
      key: plusNavBarItems.TEST_SERIES,
      isShown: true,
      icon: StyTestsIcon,
      hovColor: 'var(--color-i-blue-20)'
    },
    {
      label: 'Saved',
      dataId: plusNavBarItems.SAVED,
      isSelected: selectedNav === plusNavBarItems.SAVED,
      href: `${goalHref}/saved`,
      as: `${goalAs}/saved`,
      itemHandleClick: handleClick(
        plusNavBarItems.SAVED,
        `${goalHref}/saved`,
        `${goalAs}/saved`
      ),
      key: plusNavBarItems.SAVED,
      isShown: hasPlusSubscription,
      hovColor: 'var(--color-i-orange-11)',
      icon: StySideNavSaved
    }
  ];

  if (isRecordedGoal) {
    return algos.moveArrayElement(selfStudyMap, 3, 1);
  }

  return isGTP ? gtpSelfStudyMap : selfStudyMap;
};

const isLiveMentoringEnabled = (array = []) => {
  const ele = array.find(
    (feature) => feature.type === sideNavFlagsConst.ONE_TO_ONE_LIVE_MENTORING
  );
  return !!ele;
};

const isAADEnabled = (array = []) => {
  const ele = array.find(
    (feature) => feature.type === sideNavFlagsConst.DOUBTS_AND_SOLUTIONS
  );
  const ele2 = ele?.isAskADoubtEnabled;
  return !!ele2;
};

const isNotesEnabled = (array = []) => {
  const ele = array.find((feature) => feature.type === sideNavFlagsConst.NOTES);
  return !!ele;
};

const SelfStudy = ({
  handleClick,
  selectedNav,
  goalUID,
  goalAs,
  hasPlusSubscription,
  isCatalogueFeatureAvailable,
  hasOfflineSubscription
}) => {
  const { route } = useRouter();
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const { topology: goalInfo } = useTopology(goalUID);
  const isPracticeAvailable = getIsPracticeAvailable(goalInfo) || isGTP;
  const isNoTestGoal = getIsNoTestGoal(goalInfo);
  const isSyllabusV1Enabled = getIsSyllabusV1Enabled(goalInfo);
  const isPrimeEnabledFlag = getIsPrimeEnabled(goalInfo);
  const isIconicAvailable = getIsIconicAvailable(goalInfo);

  const { sidenavFlags } = useSidenavFlags(goalUID);
  const { features } = sidenavFlags || { features: [] };

  const { isK12, isRecordedGoal } = goalInfo;
  const liveMentoringEnabled = isLiveMentoringEnabled(features);
  const isAADEnabledFlag = isAADEnabled(features);
  const isNotesEnabledFlag = isNotesEnabled(features);
  const isPrintedNotesEnabledFlag = isPrintedNotesEnabled(features);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isSubscribedToLite = useMyInfo(getIsSubscribedToLite(goalUID));
  const isSubscribedToIconic = useMyInfo(getIsSubscribedToIconic(goalUID));
  const isSubscribedToIconicPro = useMyInfo(
    getIsSubscribedToIconicPro(goalUID)
  );
  const isPlusOnly =
    hasPlusSubscription && !isSubscribedToIconic && !isSubscribedToIconicPro;
  const isMentorshipLocked =
    isPlusOnly && !liveMentoringEnabled && isIconicAvailable;
  const isPrintedNotesLocked =
    isPlusOnly && !isPrintedNotesEnabledFlag && isIconicAvailable;
  const goalSegmentData = useGoalEventProps({ goalUID });
  const isTestRevampGoal = useConstants(getIsTestRevampGoal(goalUID));
  const isCompeteEnabled = useConstants(getIsCompeteEnabled(goalUID));
  const isReferralRevampEnabled = useConstants(getIsReferralRevampEnabled);
  const showBlueDotForTestRevamp =
    getIsBlueDotshownForTestRevamp(isTestRevampGoal);

  const tabsMap = getSelfStudyMap({
    route,
    goalUID,
    handleClick,
    isPracticeAvailable,
    isCompeteEnabled,
    isNoTestGoal,
    hasPlusSubscription,
    isSubscribedToLite,
    liveMentoringEnabled,
    isCatalogueFeatureAvailable,
    selectedNav,
    goalAs,
    isK12,
    isAADEnabledFlag,
    isNotesEnabledFlag,
    isLoggedIn,
    isSyllabusV1Enabled,
    isGTP,
    showBlueDotForTestRevamp,
    isRecordedGoal,
    isPrintedNotesEnabledFlag,
    hasOfflineSubscription,
    isPrimeEnabledFlag,
    isMentorshipLocked,
    isPrintedNotesLocked,
    isReferralRevampEnabled
  });

  const isMounted = useMounted();

  return (
    <>
      {!isGTP && (
        <StyH7 $isLite={isSubscribedToLite || isRecordedGoal}>SELF STUDY</StyH7>
      )}
      {tabsMap
        .filter((item) => item && item.isShown)
        .map((item) => {
          const onItemClick = (event) => {
            if (item.upsellType) {
              segment.track(SELF_STUDY_UPSELL_ITEM_CLICKED, {
                ...goalSegmentData,
                last_primary_source_section: 'Self Study',
                card_type: item.upsellType
              });
            }
            item.itemHandleClick?.(event);
          };
          return (
            <StyItem
              $selected={item.isSelected}
              data-id={item.dataId}
              hovColor={item.hovColor}
              key={item.key}
              href={item.href}
              as={item.as}
              onClick={onItemClick}
              prefetch
            >
              {isMounted && item.showDot && <IconDot />}
              <SideNavItemIcon
                icon={item.icon}
                keepIconColor={item.keepIconColor}
                isSelected={item.isSelected}
                hovColor={item.hovColor}
              />
              {item.showNewTag ? (
                <LabelRow>
                  <Label>{item.label}</Label>
                  <ReferralNewTag>NEW</ReferralNewTag>
                </LabelRow>
              ) : (
                <Label>{item.label}</Label>
              )}
              {item.isLocked && (
                <StyTooltip
                  title="Buy iconic/pro to unlock"
                  placement="bottom"
                  backgroundColor="var(--color-divider)"
                  borderRadius={Spacings.SPACING_4B}
                  padding={`0 ${Spacings.SPACING_3B}`}
                  maxWidth="none"
                >
                  <LockWrapper>
                    <StyLockIcon />
                  </LockWrapper>
                </StyTooltip>
              )}
            </StyItem>
          );
        })}
    </>
  );
};

export default SelfStudy;

SelfStudy.propTypes = {
  handleClick: PropTypes.func.isRequired,
  selectedNav: PropTypes.string,
  goalUID: PropTypes.string.isRequired
};
SelfStudy.defaultProps = {
  selectedNav: ''
};
